import React from "react";

import { coalesce, doNothing } from "@kazoohr/helpers";

const AUTH_STATUS_CHECK_INTERVAL = 1000 * 60 * 3;

export interface WorkOSUser {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
}

export interface WorkOSMeResponse {
  status: "authenticated" | "unauthenticated";
  user: WorkOSUser | null;
}

interface WorkOSAuthState {
  user: WorkOSUser | null;
  isLoading: boolean;
}

const WorkOSAuthContext = React.createContext<{
  user: WorkOSUser | null;
  signOut: (args?: { returnTo?: string }) => Promise<void>;
  signIn: (args?: { returnTo?: string }) => void;
  signUp: (args?: { returnTo?: string }) => void;
  isLoading: boolean;
  state: "loading" | "authenticated" | "unauthenticated";
}>({
  user: null,
  signOut: doNothing,
  signIn: doNothing,
  signUp: doNothing,
  isLoading: true,
  state: "loading",
});

export function WorkOSAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [authState, _setAuthState] = React.useState<WorkOSAuthState>(() => {
    const initialUser = window.__APP_DATA__?.workosConfig?.user || null;
    return {
      user: initialUser,
      isLoading: false, // We're not loading if user is already in __APP_DATA__
    };
  });

  const setAuthState = React.useCallback(
    // istanbul ignore next -- Defensive check to avoid re-renders, difficult to test meaningfully
    (state: WorkOSAuthState) => {
      // This looks weird, but it's to avoid unnecessary re-renders
      _setAuthState((originalState) => {
        const hasAnyChange =
          JSON.stringify(state) !== JSON.stringify(originalState);
        return hasAnyChange ? state : originalState;
      });
    },
    []
  );

  React.useEffect(() => {
    // istanbul ignore next -- Interval callback is difficult to test with fake timers
    // eslint-disable-next-line complexity
    const checkAuthStatus = async () => {
      try {
        const response = await fetch("/me", {
          credentials: "include",
        });

        if (response && response.ok) {
          const data: WorkOSMeResponse = await response.json();

          // Check if backend refreshed the token
          const tokenRefreshed = response.headers.get("X-Token-Refreshed");
          if (tokenRefreshed === "true") {
            // Backend refreshed the session, update our state
            console.debug("WorkOS token was refreshed"); // eslint-disable-line no-console
          }

          if (data.status === "authenticated" && data.user) {
            setAuthState({ user: data.user, isLoading: false });
          } else if (data.status === "unauthenticated") {
            setAuthState({ user: null, isLoading: false });
          } else {
            setAuthState({ user: null, isLoading: false });
          }
        } else if (response.status === 502 || response.status === 503) {
          // 502/503 are transient LB errors, not affirmative auth failures
          // Just log and let it retry on the next interval
          console.debug(`/me returned ${response.status}, will retry`); // eslint-disable-line no-console
        } else {
          console.error("Error logging in", response); // eslint-disable-line no-console
          setAuthState({ user: null, isLoading: false });
        }
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
        setAuthState({ user: null, isLoading: false });
      }
    };

    // If user exists, set up periodic check
    const interval = setInterval(checkAuthStatus, AUTH_STATUS_CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, [setAuthState]);

  const signIn = React.useCallback((args?: { returnTo?: string }) => {
    const returnTo = coalesce(args?.returnTo, window.location.pathname);
    window.location.href = `/sign-in?returnTo=${encodeURIComponent(returnTo)}`;
  }, []);

  const signUp = React.useCallback((args?: { returnTo?: string }) => {
    const returnTo = coalesce(args?.returnTo, window.location.pathname);
    window.location.href = `/sign-up?returnTo=${encodeURIComponent(returnTo)}`;
  }, []);

  const signOut = React.useCallback(async (args?: { returnTo?: string }) => {
    const returnTo = coalesce(args?.returnTo, window.location.pathname);
    window.location.href = `/sign-out?returnTo=${encodeURIComponent(returnTo)}`;
  }, []);

  const state = authState.isLoading
    ? /* istanbul ignore next */ "loading"
    : authState.user
    ? "authenticated"
    : "unauthenticated";

  return (
    <WorkOSAuthContext.Provider
      value={{
        user: authState.user,
        signIn,
        signOut,
        signUp,
        isLoading: authState.isLoading,
        state,
      }}
    >
      {children}
    </WorkOSAuthContext.Provider>
  );
}

export function useWorkOSAuth() {
  return React.useContext(WorkOSAuthContext);
}

export function initializeWorkOSAuth() {
  const appData = window.__APP_DATA__;
  const clientId = appData?.workosConfig?.workosClientId;
  if (!clientId) {
    throw new Error("WorkOS client ID is missing");
  }
  return { clientId };
}
